import type { ProductDeliveryType, ProductFormData, StoreProductType } from '../types/productCreation';

export interface ValidationIssue {
  field: string;
  message: string;
  step: 'card' | 'product_page' | 'delivery';
}

/** Stripe / publish floor for paid digital products. */
export const MIN_DIGITAL_PRODUCT_PRICE = 0.5;

/** Empty/0 is allowed while drafting; any positive amount must be ≥ $0.50. */
export function getDigitalProductPriceError(price: number): string {
  const amount = Number(price) || 0;
  if (amount > 0 && amount < MIN_DIGITAL_PRODUCT_PRICE) {
    return 'Minimum price is $0.50';
  }
  return '';
}

export function getPublishValidationIssues(
  productType: StoreProductType,
  formData: ProductFormData,
  deliveryType: ProductDeliveryType,
  redirectUrl: string,
  uploadedFilesCount: number
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (!formData.thumbnailUrl?.trim()) {
    issues.push({ field: 'thumbnail', message: 'Thumbnail image', step: 'card' });
  }
  if (!formData.title?.trim()) {
    issues.push({ field: 'title', message: 'Product name', step: 'card' });
  }
  if (!formData.buttonText?.trim()) {
    issues.push({ field: 'buttonText', message: 'Button text', step: 'card' });
  }

  if (productType === 'digital_product') {
    if (!formData.price || formData.price < MIN_DIGITAL_PRODUCT_PRICE) {
      issues.push({ field: 'price', message: 'Price (minimum $0.50)', step: 'product_page' });
    }
  }

  if (productType === 'freebie' && !formData.bottomTitle?.trim()) {
    issues.push({ field: 'bottomTitle', message: 'Checkout button text', step: 'product_page' });
  }

  const plainDescription = formData.description?.replace(/<[^>]*>/g, '').trim();
  if (!plainDescription) {
    issues.push({ field: 'description', message: 'Product description', step: 'product_page' });
  } else {
    const wordCount = plainDescription.split(/\s+/).filter(Boolean).length;
    if (wordCount > 1000) {
      issues.push({
        field: 'description',
        message: 'Description (max 1000 words)',
        step: 'product_page',
      });
    }
  }

  if (!formData.coverImageUrl?.trim()) {
    issues.push({ field: 'coverImage', message: 'Cover image', step: 'product_page' });
  }

  if (deliveryType === 'file' && uploadedFilesCount === 0) {
    issues.push({ field: 'file', message: 'Delivery file', step: 'delivery' });
  }
  if (deliveryType === 'redirect' && !redirectUrl?.trim()) {
    issues.push({ field: 'redirectUrl', message: 'Redirect URL', step: 'delivery' });
  }

  return issues;
}
